import { Button, Stack, Typography } from '@mui/material'
import { PlusCircle } from 'lucide-react'
import { useRef, useState } from 'react'

import { CopyPasteInputBulkImport } from './copy-paste/CopyPasteInputBulkImport'
import { PromptFieldBulkImport } from './copy-paste/PromptFieldBulkImport'
import { GenerateTSVDialog } from './copy-paste/GenerateTSVDialog'

import { bulkImportStoreActions } from '@/components/BulkImportModal/store/bulk-import.store'
import { useCardInputPromptParser } from '@/components/BulkImportModal/hooks/useCardInputPromptParser'
import { useBulkImportStore, useCardTypeBulkImportStore } from '@/components/BulkImportModal/hooks/useBulkImportStore'
import { checkTypeCardActivity } from '@/utils/checkTypeCard'
import { AIGenerateIcon } from '@/views/NewBuilder/components/IconsBuilder'

export function CopyAndPasteSectionBulkImport() {
  const ref = useRef<HTMLDivElement>(null)
  const cardType = useCardTypeBulkImportStore()
  const { isRepeat, isMC, isRespond, isShortAnswer } = checkTypeCardActivity(cardType)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { language, defaultLanguage } = useBulkImportStore(s => s.settings)

  const { generateCardEntriesFromTextarea, textareaValue, handleTextareaChange } = useCardInputPromptParser(prompts => {
    bulkImportStoreActions.convertStringsToCardDrafts(prompts)
    scrollToTop()
  }, isRepeat)

  const scrollToTop = () => {
    ref.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <Stack sx={{ gap: '10px', height: '100%', overflow: 'hidden' }}>
      <Typography fontWeight="semibold" variant="xs">
        Import Your Data:{' '}
        <span style={{ fontWeight: 'normal' }}>
          Copy and Paste your data here (from Word, Excel, Google Docs, etc.)
        </span>
      </Typography>
      <CopyPasteInputBulkImport
        handleTextareaChange={handleTextareaChange}
        placeholder={
          isRepeat
            ? 'Target Text   Translation (optional) #1 \nTarget Text   Translation (optional) #2 \nTarget Text   Translation (optional) #3'
            : isMC
              ? 'Question #1 \nQuestion #2 \nQuestion #3'
              : isRespond || isShortAnswer
                ? 'Prompt #1 \nPrompt #2 \nPrompt #3'
                : ''
        }
        textareaValue={textareaValue}
        onCardsGenerated={scrollToTop}
      />
      <Stack direction="row" gap="10px" justifyContent="flex-end">
        <Button
          startIcon={<AIGenerateIcon />}
          sx={() => ({
            borderRadius: '24px',
            background: '#FFF',
            border: '1px solid transparent',
            backgroundClip: 'padding-box',
            padding: '8px 12px',
            '&::before': {
              content: '""',
              position: 'absolute',
              borderRadius: '16px',
              padding: '1px',
              background: 'linear-gradient(45deg, #FF6C6C, #DD7BFF)',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'destination-out',
              maskComposite: 'exclude',
              pointerEvents: 'none',
            },
          })}
          onClick={() => {
            setIsDialogOpen(true)
          }}
        >
          <Typography
            fontWeight={500}
            sx={{
              textTransform: 'none',
              backgroundColor: '#fff',
              color: 'transparent',
              backgroundImage: 'linear-gradient(45deg, #FF6C6C 0%, #DD7BFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Generate with AI
          </Typography>
        </Button>
        <Button
          disabled={!textareaValue}
          sx={{
            width: 'fit-content',
          }}
          variant="contained"
          onClick={generateCardEntriesFromTextarea}
        >
          Add
        </Button>
      </Stack>

      <Stack
        ref={ref}
        sx={{
          overflowY: 'auto',
          flex: '1 0 0',
          gap: '10px',
        }}
      >
        <PromptFieldBulkImport />
      </Stack>
      <Button
        startIcon={<PlusCircle size={20} />}
        sx={{
          width: 'fit-content',
          color: '#C11574',
        }}
        onClick={() => {
          bulkImportStoreActions.addBlankCardDraftEntry()
          scrollToTop()
        }}
      >
        Add
      </Button>
      <GenerateTSVDialog
        cardType={cardType}
        language={language}
        open={isDialogOpen}
        translateTo={defaultLanguage}
        onClose={() => {
          setIsDialogOpen(false)
        }}
        onTSVGenerated={handleTextareaChange}
      />
    </Stack>
  )
}
